<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationActionsPanel from 'dashboard/components-next/Conversation/ConversationActionsPanel.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import GgFollowupPanel from 'dashboard/components-next/gg-followup/GgFollowupPanel.vue';
import GgSupportPanel from 'dashboard/components-next/gg-support/GgSupportPanel.vue';
import GgSupportClosingFollowup from 'dashboard/components-next/gg-support/GgSupportClosingFollowup.vue';
import { useGgFollowupSelection } from 'dashboard/composables/useGgFollowup';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationActionsPanel,
    ConversationSidebar,
    GgFollowupPanel,
    GgSupportPanel,
    GgSupportClosingFollowup,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings, isOnExpandedLayout } =
      useUISettings();
    const { accountId } = useAccount();
    const { selection: ggSelection } = useGgFollowupSelection();

    return {
      uiSettings,
      updateUISettings,
      isOnExpandedLayout,
      accountId,
      ggSelection,
    };
  },
  data() {
    return {
      showSearchModal: false,
      isActionsPanelOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    showGgFollowup() {
      return (
        Boolean(this.ggSelection.conversationId) &&
        this.ggSelection.conversationId === this.currentChat.id
      );
    },
    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
  },
  watch: {
    conversationId() {
      this.isActionsPanelOpen = false;
      this.fetchConversationIfUnavailable();
    },
    showGgFollowup(isOpen) {
      if (isOpen) this.isActionsPanelOpen = false;
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full min-h-0 min-w-0 overflow-hidden">
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
    />
    <ConversationBox
      v-if="showMessageView"
      :class="{ 'pb-[45dvh] md:pb-0': showGgFollowup }"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch
        v-if="currentChat.id && !showGgFollowup"
        v-model:actions-open="isActionsPanelOpen"
      />
    </ConversationBox>
    <GgSupportClosingFollowup
      v-if="showGgFollowup && ggSelection.closingSupport"
      :key="`${currentChat.id}-${ggSelection.supportTicketId}`"
      :conversation-id="currentChat.id"
      :ticket-id="ggSelection.supportTicketId"
    />
    <GgSupportPanel
      v-else-if="showGgFollowup && ggSelection.mode === 'support'"
      :key="currentChat.id"
      :conversation-id="currentChat.id"
    />
    <GgFollowupPanel
      v-else-if="showGgFollowup"
      :key="currentChat.id"
      :conversation-id="currentChat.id"
    />
    <ConversationActionsPanel
      :conversation-id="currentChat.id"
      v-else-if="isActionsPanelOpen && currentChat.id"
      @close="isActionsPanelOpen = false"
    />
    <ConversationSidebar
      v-else-if="shouldShowSidebar"
      :current-chat="currentChat"
    />
    <CmdBarConversationSnooze />
  </section>
</template>
